// Visual card for one previously-stitched design (one spot from one cart
// line). Renders the design content (text in actual font, or logo
// filename, or both) + the placement label + size + source label. Click
// → pre-populate the editor's draft with this design's data.

import type { Config } from "../types";
import type { ReusableDesign } from "../reuse";

export function DesignCard({
  design,
  config,
  onPick,
}: {
  design: ReusableDesign;
  config: Config;
  onPick: () => void;
}) {
  const draft = design.draft;
  const placement = config.placements.find((p) => p.slug === draft.placementSlug);
  const font = draft.fontId ? config.fonts.find((f) => f.id === draft.fontId) : null;
  // Phase 32b: uppercase-only fonts (College, Stencil Block) preview in
  // ALL CAPS even if the source spot's text was mixed case. Matches
  // what'll actually stitch on the new order.
  const previewText = font?.forceUppercase
    ? draft.text.toUpperCase()
    : draft.text;

  // A reusable design always has SOME content, so never render an empty
  // card. Prefer the main upload filename, then any extra-upload filename
  // (pet photos land there), then a generic label when only a staging key
  // is present.
  const logoLabel =
    draft.uploadFilename ||
    draft.extraUploads?.find((u) => u.filename)?.filename ||
    null;
  const hasUpload =
    !!draft.uploadStagingKey ||
    (!!draft.extraUploads && draft.extraUploads.length > 0);
  const showText =
    (draft.mode === "text" || draft.mode === "both") && !!draft.text;
  const showLogo =
    (draft.mode === "logo" || draft.mode === "both") && (!!logoLabel || hasUpload);

  return (
    <button type="button" className="rk-pc-design-card" onClick={onPick} title="Use this design">
      <div className="rk-pc-design-card__canvas">
        {showText ? (
          <span
            className="rk-pc-design-card__text"
            style={{
              fontFamily: font ? `'${font.family}', system-ui, sans-serif` : "system-ui, sans-serif",
              letterSpacing: font?.tracking ?? "0",
            }}
          >
            {previewText.slice(0, 14)}
          </span>
        ) : null}
        {showLogo ? (
          <span className="rk-pc-design-card__logo">
            <span className="rk-pc-design-card__logo-glyph">▣</span>
            <small>{logoLabel ?? "Uploaded design"}</small>
          </span>
        ) : null}
        {!showText && !showLogo ? (
          <span className="rk-pc-design-card__logo">
            <span className="rk-pc-design-card__logo-glyph">▣</span>
            <small>Saved design</small>
          </span>
        ) : null}
      </div>
      <div className="rk-pc-design-card__meta">
        <span className="rk-pc-design-card__placement">
          {placement?.name ?? draft.placementSlug ?? "Unset"}
          <small>{draft.sizeMm}mm</small>
        </span>
        <span className="rk-pc-design-card__from">
          From <small>{design.sourceLabel}</small>
        </span>
      </div>
    </button>
  );
}
